import {
  Button,
  CircularProgress,
  IconButton,
  Input,
  Stack, Typography,
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Message01Icon, SendIcon, SparklesIcon} from "@hugeicons/core-free-icons";
import { grey } from "@mui/material/colors";
import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {LikePopoverButton} from "./like-popover-button";
import {ConversationComments} from "./conversation-comments";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {momentHash} from "../../../../utils/momentHash";
import {useRegistryProject} from "../../../registry-project/RegistryProjectContext";
import {PillSelect} from "../../../registry-project/dashboard/components/Panel";

export const ConversationInteractions = ({ conversation, updateConversation }) => {
  const [showComments, setShowComments] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [captureTopics, setCaptureTopics] = useState([]);
  const [captureTopicsLoading, setCaptureTopicsLoading] = useState(false);
  const [captureTopicId, setCaptureTopicId] = useState("");
  const [captureContent, setCaptureContent] = useState("");
  const [captureSubmitting, setCaptureSubmitting] = useState(false);

  // Only meaningful inside a registry project (the Knowledge Manager backend is scoped to one) -
  // outside that context (legacy project pages) useRegistryProject() falls back to its default
  // {project: null, ...} value, so the button stays hidden instead of erroring.
  const {project} = useRegistryProject();

  useEffect(() => {
    if (!showCapture || !project?.id || captureTopics.length > 0) return;
    setCaptureTopicsLoading(true);
    KnowledgeManagerRepository.listTopics({organizationId: project.organizationId, projectId: String(project.id)})
      .then(list => {
        setCaptureTopics(list);
        if (list.length === 1) setCaptureTopicId(list[0].id);
      })
      .catch(() => toast.error("Failed to load knowledge base topics"))
      .finally(() => setCaptureTopicsLoading(false));
  }, [showCapture, project?.id, project?.organizationId, captureTopics.length]);

  const handleCapture = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!captureTopicId || !captureContent.trim()) return;
    setCaptureSubmitting(true);
    KnowledgeManagerRepository.uploadTextDocument(captureTopicId, {
      content: captureContent,
      fileName: `captured-${momentHash()}.md`,
    })
      .then(() => {
        toast.success("Added to the knowledge base");
        setCaptureContent("");
      })
      .catch(err => toast.error(err.response?.data?.message || "Failed to capture knowledge"))
      .finally(() => setCaptureSubmitting(false));
  };

  return (
    <Stack
      direction="column"
      alignItems="flex-start"
      maxWidth="sm" gap={3}
      sx={{ mx: "auto", width: "100%", pb: 5 }}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%" }}>
        <LikePopoverButton
          conversation={conversation}
          updateConversation={updateConversation}
        />
        <Button
          startIcon={<HugeiconsIcon icon={Message01Icon} size={16} />}
          sx={{ color: showComments || conversation?.comments?.length > 0 ? "info.main" : grey[700], px: 1 }}
          color={showComments ? "info" : "secondary"}
          onClick={() => setShowComments(prev => !prev)}
        >
          Comment {conversation?.comments?.length ? `(${conversation?.comments?.length})` : null}
        </Button>
        {project?.id && (
          <Button
            startIcon={<HugeiconsIcon icon={SparklesIcon} size={16} />}
            sx={{ color: showCapture ? "info.main" : grey[700], px: 1 }}
            color={showCapture ? "info" : "secondary"}
            onClick={() => setShowCapture(prev => !prev)}
          >
            Quick Note
          </Button>
        )}
      </Stack>

      {showComments && <ConversationComments conversation={conversation} updateConversation={updateConversation} />}

      {showCapture && (
        <Stack
          component={"form"} onSubmit={handleCapture} direction={"row"} alignItems={"center"} gap={1}
          className={"border-shadow"}
          sx={{ p: 1.5, borderRadius: 5, width: "90%", mb: 2, mx: "auto", bgcolor: "background.paper" }}
        >
          <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{flexShrink: 0}}>
            <Typography sx={{fontSize: 11, color: grey[500], whiteSpace: "nowrap"}}>Topic:</Typography>
            <PillSelect
              value={captureTopicId}
              onChange={setCaptureTopicId}
              options={captureTopics.map(t => ({value: t.id, label: t.name}))}
              placeholder={captureTopicsLoading ? "Loading…" : "Select"}
              ariaLabel={"Knowledge base topic"}
            />
          </Stack>
          <Input
            placeholder={"What should the model learn?"} disableUnderline
            sx={{flex: 1}} maxRows={3} multiline
            value={captureContent}
            onChange={(e) => setCaptureContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleCapture(e);
              }
            }}
          />
          <IconButton disabled={!captureTopicId || !captureContent.trim() || captureSubmitting} type={"submit"}>
            {captureSubmitting ? <CircularProgress size={18}/> : <HugeiconsIcon icon={SendIcon}/>}
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};
